import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs, organizationLd, webSiteLd, datasetLd } from '../seo';
import cities from '../data/cities.json';
import localitiesData from '../data/localities.json';

interface CityStats {
  city: { slug: string; name: string; state: string };
  stats: {
    listings: {
      count: number;
      avg_hourly: number | null;
      avg_daily: number | null;
      avg_monthly: number | null;
    };
    by_vehicle_type: Record<string, number>;
  } | null;
}

interface LocalityLite {
  slug: string;
  name: string;
}

export default function CityPricePage() {
  const { city: citySlug = '' } = useParams<{ city: string }>();
  const [data, setData] = useState<CityStats | null>(null);
  const [localities, setLocalities] = useState<LocalityLite[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string>('');

  useEffect(() => {
    fetch(`/api/stats/city/${citySlug}`)
      .then((r) => r.json())
      .then((j) => {
        if (j?.data) setData(j.data);
      })
      .catch(() => undefined);
    const locs =
      (localitiesData as { city: string; localities: LocalityLite[] }[]).find(
        (g) => g.city === citySlug
      )?.localities ?? [];
    setLocalities(locs);
    setUpdatedAt(new Date().toISOString());
  }, [citySlug]);

  const city = (cities as { slug: string; name: string; state: string }[]).find((c) => c.slug === citySlug);
  if (!city) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-2xl font-bold text-gray-900">City not found</h1>
        <p className="text-gray-600 mt-2">No price data for "{citySlug}".</p>
        <Link to="/prices" className="text-parksy-600 hover:underline mt-4 inline-block">
          Back to all prices →
        </Link>
      </div>
    );
  }

  const pageTitle = `Parking Prices in ${city.name} — Monthly, Daily, Hourly | Parksy`;
  const description = `Live, crowdsourced parking prices in ${city.name}, ${city.state}. Monthly, daily, and hourly rates by vehicle type, plus per-locality breakdowns.`;
  const canonical = `https://parksy.rent/prices/${city.slug}`;

  return (
    <>
      <Breadcrumbs
        items={[
          { label: 'Home', to: '/' },
          { label: 'Prices', to: '/prices' },
          { label: city.name },
        ]}
        pageTitle={pageTitle}
        pageDescription={description}
        extraSchemas={[
          organizationLd(),
          webSiteLd(),
          datasetLd({
            name: `${city.name} parking prices`,
            description: `Live parking price data for ${city.name}, ${city.state}.`,
            url: canonical,
            citySlug: city.slug,
            updatedAt,
            rowCount: data?.stats?.listings.count,
          }),
        ]}
      />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Parking prices in {city.name}</h1>
          <p className="text-gray-600">
            {city.state}. Updated continuously from crowdsourced pins.
          </p>
        </header>

        {data?.stats?.listings.count ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <Stat label="Listings" value={String(data.stats.listings.count)} />
            <Stat
              label="Avg ₹/month"
              value={data.stats.listings.avg_monthly ? `₹${Math.round(data.stats.listings.avg_monthly).toLocaleString('en-IN')}` : '—'}
            />
            <Stat
              label="Avg ₹/day"
              value={data.stats.listings.avg_daily ? `₹${Math.round(data.stats.listings.avg_daily).toLocaleString('en-IN')}` : '—'}
            />
            <Stat
              label="Avg ₹/hour"
              value={data.stats.listings.avg_hourly ? `₹${Math.round(data.stats.listings.avg_hourly).toLocaleString('en-IN')}` : '—'}
            />
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <h2 className="font-semibold text-amber-900 mb-1">No price data yet for {city.name}</h2>
            <p className="text-amber-800 text-sm mb-3">
              Be the first to drop a price pin in {city.name} and unlock market-rate data for the rest of the city.
            </p>
            <Link
              to="/"
              className="inline-block bg-parksy-600 hover:bg-parksy-700 text-white font-medium px-4 py-2 rounded-lg text-sm"
            >
              Open the map →
            </Link>
          </div>
        )}

        {Object.keys(data?.stats?.by_vehicle_type ?? {}).length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">By vehicle type</h2>
            <div className="bg-white border rounded-xl p-4 text-sm">
              <div className="flex flex-wrap gap-2">
                {Object.entries(data!.stats!.by_vehicle_type).map(([vt, count]) => (
                  <span key={vt} className="bg-gray-50 border rounded-lg px-3 py-1 text-gray-700">
                    {vt}: <strong>{count}</strong>
                  </span>
                ))}
              </div>
            </div>
          </section>
        )}

        {localities.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">By locality</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-sm">
              {localities.map((l) => (
                <Link
                  key={l.slug}
                  to={`/prices/${city.slug}/${l.slug}`}
                  className="bg-white border rounded-lg px-3 py-2 text-gray-700 hover:border-parksy-500 hover:text-parksy-700"
                >
                  {l.name} prices
                </Link>
              ))}
            </div>
          </section>
        )}

        <p className="text-xs text-gray-500">
          Data is crowdsourced and may contain outliers. Parksy filters reports and applies
          automatic anomaly detection. Pin what you pay to improve the dataset for your area.
        </p>
      </div>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border rounded-xl p-4 text-center">
      <div className="text-2xl font-bold text-parksy-600">{value}</div>
      <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">{label}</div>
    </div>
  );
}
